import React, { useState } from 'react';
import './Settings.css';

function Settings() {
  const [connections, setConnections] = useState([
    {
      id: 1,
      name: 'Production Tableau Server',
      type: 'Tableau Server',
      url: 'https://tableau-prod.company.com',
      authMethod: 'username_password',
      username: 'admin@company.com',
      verified: true,
      lastTested: '2025-12-22'
    },
    {
      id: 2,
      name: 'Staging Cloud Environment',
      type: 'Tableau Cloud',
      url: 'https://tableau-staging.company.com',
      authMethod: 'pat',
      tokenName: 'staging-token',
      verified: true,
      lastTested: '2025-12-20'
    },
    {
      id: 3,
      name: 'Development Server',
      type: 'Tableau Server',
      url: 'https://tableau-dev.company.com',
      authMethod: 'connected_app',
      clientId: 'dev-app-client',
      verified: false,
      lastTested: '2025-12-15'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newConnection, setNewConnection] = useState({
    name: '',
    type: 'Tableau Server',
    url: '',
    authMethod: 'username_password',
    username: '',
    password: '',
    tokenName: '',
    tokenSecret: '',
    clientId: '',
    secretId: '',
    secretValue: ''
  });

  const handleAddConnection = (e) => {
    e.preventDefault();
    const connection = {
      id: connections.length + 1,
      name: newConnection.name,
      type: newConnection.type,
      url: newConnection.url,
      authMethod: newConnection.authMethod,
      verified: false,
      lastTested: 'Never'
    };

    // Add auth-specific fields based on method
    if (newConnection.authMethod === 'username_password') {
      connection.username = newConnection.username;
    } else if (newConnection.authMethod === 'pat') {
      connection.tokenName = newConnection.tokenName;
    } else if (newConnection.authMethod === 'connected_app') {
      connection.clientId = newConnection.clientId;
    }

    setConnections([...connections, connection]);
    setNewConnection({
      name: '',
      type: 'Tableau Server',
      url: '',
      authMethod: 'username_password',
      username: '',
      password: '',
      tokenName: '',
      tokenSecret: '',
      clientId: '',
      secretId: '',
      secretValue: ''
    });
    setShowAddForm(false);
  };

  const handleTestConnection = (id) => {
    // Simulate successful connection test
    setConnections(connections.map(conn =>
      conn.id === id
        ? { ...conn, verified: true, lastTested: new Date().toISOString().split('T')[0] }
        : conn
    ));
    alert('Connection test successful! Connection has been verified.');
  };

  const handleDeleteConnection = (id) => {
    if (window.confirm('Are you sure you want to delete this connection?')) {
      setConnections(connections.filter(conn => conn.id !== id));
    }
  };

  const getAuthMethodLabel = (method) => {
    switch(method) {
      case 'username_password': return 'Username/Password';
      case 'pat': return 'Personal Access Token';
      case 'connected_app': return 'Connected App';
      default: return method;
    }
  };

  const getAuthIdentifier = (connection) => {
    switch(connection.authMethod) {
      case 'username_password': return connection.username;
      case 'pat': return connection.tokenName;
      case 'connected_app': return connection.clientId;
      default: return 'N/A';
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div>
          <h1>Settings</h1>
          <p>Manage your Tableau server connections</p>
        </div>
        <button
          className="primary-button"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Connection'}
        </button>
      </div>

      {showAddForm && (
        <div className="add-connection-form">
          <h2>New Tableau Connection</h2>
          <form onSubmit={handleAddConnection}>
            <div className="form-row">
              <div className="form-group">
                <label>Connection Name</label>
                <input
                  type="text"
                  value={newConnection.name}
                  onChange={(e) => setNewConnection({ ...newConnection, name: e.target.value })}
                  placeholder="e.g., Production Server"
                  required
                />
              </div>
              <div className="form-group">
                <label>Connection Type</label>
                <select
                  value={newConnection.type}
                  onChange={(e) => setNewConnection({ ...newConnection, type: e.target.value })}
                  required
                >
                  <option value="Tableau Server">Tableau Server</option>
                  <option value="Tableau Cloud">Tableau Cloud</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Server URL</label>
                <input
                  type="url"
                  value={newConnection.url}
                  onChange={(e) => setNewConnection({ ...newConnection, url: e.target.value })}
                  placeholder="https://tableau.company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Authentication Method</label>
                <select
                  value={newConnection.authMethod}
                  onChange={(e) => setNewConnection({ ...newConnection, authMethod: e.target.value })}
                  required
                >
                  <option value="username_password">Username/Password</option>
                  <option value="pat">Personal Access Token (PAT)</option>
                  <option value="connected_app">Connected App</option>
                </select>
              </div>
            </div>

            {/* Username/Password Fields */}
            {newConnection.authMethod === 'username_password' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={newConnection.username}
                    onChange={(e) => setNewConnection({ ...newConnection, username: e.target.value })}
                    placeholder="username@company.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={newConnection.password}
                    onChange={(e) => setNewConnection({ ...newConnection, password: e.target.value })}
                    placeholder="Enter password"
                    required
                  />
                </div>
              </div>
            )}

            {/* PAT Fields */}
            {newConnection.authMethod === 'pat' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Token Name</label>
                  <input
                    type="text"
                    value={newConnection.tokenName}
                    onChange={(e) => setNewConnection({ ...newConnection, tokenName: e.target.value })}
                    placeholder="Enter token name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Token Secret</label>
                  <input
                    type="password"
                    value={newConnection.tokenSecret}
                    onChange={(e) => setNewConnection({ ...newConnection, tokenSecret: e.target.value })}
                    placeholder="Enter token secret"
                    required
                  />
                </div>
              </div>
            )}

            {/* Connected App Fields */}
            {newConnection.authMethod === 'connected_app' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Client ID</label>
                    <input
                      type="text"
                      value={newConnection.clientId}
                      onChange={(e) => setNewConnection({ ...newConnection, clientId: e.target.value })}
                      placeholder="Enter client ID"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Secret ID</label>
                    <input
                      type="text"
                      value={newConnection.secretId}
                      onChange={(e) => setNewConnection({ ...newConnection, secretId: e.target.value })}
                      placeholder="Enter secret ID"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Secret Value</label>
                    <input
                      type="password"
                      value={newConnection.secretValue}
                      onChange={(e) => setNewConnection({ ...newConnection, secretValue: e.target.value })}
                      placeholder="Enter secret value"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" className="primary-button">
                Add Connection
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="connections-section">
        <h2>Tableau Connections</h2>
        <div className="connections-grid">
          {connections.map((connection) => (
            <div key={connection.id} className="connection-card">
              <div className="connection-header">
                <div>
                  <h3>{connection.name}</h3>
                  <span className="connection-type">{connection.type}</span>
                </div>
                <span className={`status-badge ${connection.verified ? 'verified' : 'unverified'}`}>
                  {connection.verified ? 'Verified' : 'Unverified'}
                </span>
              </div>
              <div className="connection-details">
                <div className="detail-row">
                  <span className="detail-label">URL:</span>
                  <span className="detail-value">{connection.url}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Auth Method:</span>
                  <span className="detail-value">{getAuthMethodLabel(connection.authMethod)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">
                    {connection.authMethod === 'username_password' ? 'Username:' :
                     connection.authMethod === 'pat' ? 'Token Name:' : 'Client ID:'}
                  </span>
                  <span className="detail-value">{getAuthIdentifier(connection)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Last Tested:</span>
                  <span className="detail-value">{connection.lastTested}</span>
                </div>
              </div>
              <div className="connection-actions">
                <button
                  className="action-button test"
                  onClick={() => handleTestConnection(connection.id)}
                >
                  Test Connection
                </button>
                <button
                  className="action-button edit"
                  onClick={() => alert('Edit functionality would be implemented here')}
                >
                  Edit
                </button>
                <button
                  className="action-button delete"
                  onClick={() => handleDeleteConnection(connection.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="general-settings">
        <h2>General Settings</h2>
        <div className="settings-card">
          <div className="setting-item">
            <div className="setting-info">
              <h3>Automatic Backups</h3>
              <p>Automatically backup conversion results and analysis data</p>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" defaultChecked />
              <span className="slider"></span>
            </label>
          </div>
          <div className="setting-item">
            <div className="setting-info">
              <h3>Email Notifications</h3>
              <p>Receive email notifications when conversions complete</p>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" defaultChecked />
              <span className="slider"></span>
            </label>
          </div>
          <div className="setting-item">
            <div className="setting-info">
              <h3>Detailed Logging</h3>
              <p>Enable detailed logging for conversion and analysis processes</p>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
